import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import useSWR from 'swr'
import { api } from '../services/api'
import { useDebounce } from '../hooks'
import { Pagination, Button } from '../components'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import buttonStyles from '../components/Button.module.scss'
import styles from './TeamsList.module.scss'

interface TeamPlayer {
  tc_competitor_id: string
  tc_name: string
  ext_name: string
  tc_short_name: string
  ext_short_name: string
}

interface Team {
  tc_team_id: string
  ext_competitor_id: string
  tc_name: string
  ext_name: string
  tc_abbreviation: string
  ext_abbreviation: string
  player1: TeamPlayer
  player2: TeamPlayer
  sport_name: string
  catalog_source_name: string
  created_at: string
  updated_at: string
}

interface TeamsResponse {
  teams: Team[]
  pagination: {
    page: number
    limit: number
    total_count: number
    total_pages: number
    has_next: boolean
    has_prev: boolean
  }
  filters: {
    name: string
  }
  timestamp: string
}

const TeamsList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 100

  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const { data, isLoading, error, mutate } = useSWR(
    ['teams', { page: currentPage, limit: pageSize, name: debouncedSearchTerm }],
    () => api.get('/teams', { page: currentPage, limit: pageSize, name: debouncedSearchTerm?.trim() || undefined }),
    { revalidateOnFocus: false }
  )

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm])

  const clearSearch = () => {
    setSearchTerm('')
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading teams</h2>
          <p>Failed to fetch teams: {(error as Error).message}</p>
          <Button variant="danger" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  const teamsData: TeamsResponse = data?.body ? JSON.parse(data.body) : data
  const teams = teamsData?.teams || []
  const pagination = teamsData?.pagination

  const getTeamName = (team: Team) => {
    return team.tc_name || team.ext_name || 'Unknown Team'
  }

  const getTeamAbbreviation = (team: Team) => {
    return team.tc_abbreviation || team.ext_abbreviation || ''
  }

  const getExternalId = (team: Team) => {
    return team.ext_competitor_id || 'N/A'
  }

  const getPlayerName = (player: TeamPlayer | null) => {
    if (!player) return 'N/A'
    return player.tc_name || player.ext_name || 'Unknown Player'
  }

  const getPlayerShortName = (player: TeamPlayer | null) => {
    if (!player) return ''
    return player.tc_short_name || player.ext_short_name || ''
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.headerButtons}>
        <Link
          to="/teams/new"
          className={`${buttonStyles.button} ${buttonStyles.primary} ${buttonStyles.medium}`}
        >
          + Create Team
        </Link>
      </div>

      <div className={pageStyles.pageHeader}>
        <h1>Teams</h1>
      </div>

      {/* Search and Filters */}
      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          <div className={listTableStyles.searchInputGroup}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search teams by name..."
              className={listTableStyles.searchInput}
            />
            {searchTerm && (
              <button type="button" onClick={clearSearch} className={listTableStyles.clearButton}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading teams...</h3>
          <p>Please wait while we fetch the team data.</p>
        </div>
      ) : teams.length === 0 ? (
        <div className={listTableStyles.noResults}>
          <h3>No teams found</h3>
          <p>
            {debouncedSearchTerm
              ? `No teams match "${debouncedSearchTerm}". Try a different search term.`
              : 'Unable to load team data.'
            }
          </p>
        </div>
      ) : (
        <>
          <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
            <div>Team Name</div>
            <div>External ID</div>
            <div>Player 1</div>
            <div>Player 2</div>
            <div>Actions</div>
          </div>

          <div className={listTableStyles.tableBody}>
            {teams.map((team) => (
              <div key={team.tc_team_id} className={`${listTableStyles.tableRow} ${styles.listItem}`}>
                <div className={listTableStyles.cellPrimary}>
                  <p className={listTableStyles.primaryText}>
                    {getTeamName(team)}
                  </p>
                  {getTeamAbbreviation(team) && (
                    <p className={listTableStyles.secondaryText}>({getTeamAbbreviation(team)})</p>
                  )}
                </div>

                <div className={listTableStyles.cellId}>
                  {getExternalId(team)}
                </div>

                <div className={listTableStyles.cellText}>
                  <p className={listTableStyles.primaryText}>
                    {getPlayerName(team.player1)}
                  </p>
                  {getPlayerShortName(team.player1) && (
                    <p className={listTableStyles.secondaryText}>{getPlayerShortName(team.player1)}</p>
                  )}
                </div>

                <div className={listTableStyles.cellText}>
                  <p className={listTableStyles.primaryText}>
                    {getPlayerName(team.player2)}
                  </p>
                  {getPlayerShortName(team.player2) && (
                    <p className={listTableStyles.secondaryText}>{getPlayerShortName(team.player2)}</p>
                  )}
                </div>

                <div className={listTableStyles.cellActions}>
                  <Link
                    to={`/teams/${team.tc_team_id}`}
                    className={`${buttonStyles.button} ${buttonStyles.primary} ${buttonStyles.medium}`}
                  >
                    View
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {pagination && (
            <Pagination
              pagination={pagination}
              onPageChange={setCurrentPage}
              itemName="teams"
            />
          )}
        </>
      )}
    </div>
  )
}

export default TeamsList
